/**
 * Legacy PoseData → CanonicalFrame stream bridge.
 *
 * Converts the in-memory `PoseData` object the existing pipeline
 * produces (mediapipe-33 only, today) into the canonical schema so the
 * ROM Core + ViewModel layers can consume saved analyses without
 * waiting for the full preprocessor rewrite. Used by Phase 6 and as
 * the foundation for the Phase 7 persistence migration.
 *
 * docs/architecture-spec.md §5.3 (file-replay).
 */

import {
  CANONICAL_SCHEMA_VERSION,
  type AdapterCapabilities,
  type CanonicalFrame,
  type Landmark,
  type StreamHeader,
} from '../../canonical';
import type { CanonicalJointKey } from '../../canonical/JointKeys';
import type { PoseData } from '../../viewer/PoseData';

const ADAPTER_VERSION = '0.1.0';

const LEGACY_CAPABILITIES: AdapterCapabilities = {
  pose: true,
  pose3DWorld: false,
  pose3DLocal: true,
  depth: false,
  segmentation: false,
  cameraIntrinsics: false,
  cameraExtrinsics: false,
  arAnchors: false,
  realtime: false,
  multiPerson: false,
  persistentTracking: false,
  pii: {
    faceLandmarks: true,
    faceSegmentation: false,
    depthMap: false,
    deviceFingerprint: false,
    arWorldAnchors: false,
  },
};

/** Mapping from legacy MediaPipe 33pt index → canonical joint key.
 *  Mirrors the live MediaPipe Skeleton Normalizer; kept here so the
 *  legacy-data path stays self-contained and bit-identical given the
 *  same input. */
const LEGACY_DIRECT_MAPPING: readonly (CanonicalJointKey | null)[] = [
  'head',
  null,
  'leftEye',
  null,
  null,
  'rightEye',
  null,
  'leftEar',
  'rightEar',
  null,
  null,
  'leftShoulder',
  'rightShoulder',
  'leftElbow',
  'rightElbow',
  'leftWrist',
  'rightWrist',
  null,
  null,
  'leftHand',
  'rightHand',
  null,
  null,
  'leftHip',
  'rightHip',
  'leftKnee',
  'rightKnee',
  'leftAnkle',
  'rightAnkle',
  'leftHeel',
  'rightHeel',
  'leftToe',
  'rightToe',
];

const LEGACY_NATIVE_NAMES: readonly string[] = [
  'nose',
  'left_eye_inner',
  'left_eye',
  'left_eye_outer',
  'right_eye_inner',
  'right_eye',
  'right_eye_outer',
  'left_ear',
  'right_ear',
  'mouth_left',
  'mouth_right',
  'left_shoulder',
  'right_shoulder',
  'left_elbow',
  'right_elbow',
  'left_wrist',
  'right_wrist',
  'left_pinky',
  'right_pinky',
  'left_index',
  'right_index',
  'left_thumb',
  'right_thumb',
  'left_hip',
  'right_hip',
  'left_knee',
  'right_knee',
  'left_ankle',
  'right_ankle',
  'left_heel',
  'right_heel',
  'left_foot_index',
  'right_foot_index',
];

export interface LegacyConvertOpts {
  /** Stable ID for this synthetic stream. */
  streamId: string;
  /** Hash, never raw. */
  deviceIdHash: string;
  /** Optional 3x3 row-major rotation matrix that takes camera-frame
   *  vectors into a gravity-aligned world frame. When supplied the
   *  per-landmark `pos3DLocal` is pre-multiplied by this matrix so
   *  downstream ROM / 3D consumers see metric coords with +Y aligned
   *  to gravity instead of camera pitch. Restricted to MediaPipe-
   *  derived world coords (this is the only path here); MotionBERT
   *  output stays untouched and is rotated separately when wired. */
  gravityRotation?: readonly number[] | null;
}

export interface LegacyConvertResult {
  header: StreamHeader;
  frames: CanonicalFrame[];
  /** Diagnostics: was a gravity rotation actually applied to the
   *  produced frames? Caller can surface this in a save-pipeline
   *  summary or persistence sidecar. */
  gravityApplied: boolean;
}

export function legacyPoseDataToCanonical(
  data: PoseData,
  opts: LegacyConvertOpts,
): LegacyConvertResult {
  const numFrames = data.meta.numFrames;
  const numNodes = data.meta.numNodes;
  const sourceWidthPx = data.meta.resolution?.width ?? 1;
  const sourceHeightPx = data.meta.resolution?.height ?? 1;
  const startedAtMonotonicNs = 0;
  // Gravity rotation: the host passes a 3x3 row-major matrix from
  // GravityAnchor.buildGravityRotation(). Identity (or omitted)
  // means the path is a no-op. The matrix is applied AFTER MediaPipe's
  // y-down → y-up flip below so the +Y axis ends up aligned with
  // gravity rather than the camera's pitch.
  const gR = opts.gravityRotation ?? null;
  const gravityApplied = !!(gR && gR.length === 9 && !isIdentity3x3(gR));

  const header: StreamHeader = {
    schemaVersion: CANONICAL_SCHEMA_VERSION,
    streamId: opts.streamId,
    startedAt: {
      monotonicEpochNs: startedAtMonotonicNs,
      wallclockMs: null,
    },
    adapter: {
      id: `legacy-pose-data@${ADAPTER_VERSION}`,
      version: ADAPTER_VERSION,
      capabilities: LEGACY_CAPABILITIES,
      confidenceModel: 'visibility',
    },
    device: {
      id: opts.deviceIdHash,
      sensorIds: [],
    },
  };

  const frames: CanonicalFrame[] = new Array(numFrames);
  for (let f = 0; f < numFrames; f += 1) {
    const off = f * numNodes;
    const landmarks: Landmark[] = [];
    for (let i = 0; i < numNodes && i < LEGACY_DIRECT_MAPPING.length; i += 1) {
      const jointKey = LEGACY_DIRECT_MAPPING[i];
      const score = data.score[off + i] ?? 0;
      const x = data.x[off + i];
      const y = data.y[off + i];
      const wx = data.worldX?.[off + i];
      const wy = data.worldY?.[off + i];
      const wz = data.worldZ?.[off + i];
      const wv = data.worldVisibility?.[off + i];
      landmarks.push({
        jointKey,
        raw: { nativeName: LEGACY_NATIVE_NAMES[i] ?? `idx_${i}`, nativeIndex: i },
        pos2D: {
          xPx: x * sourceWidthPx,
          yPx: y * sourceHeightPx,
          sourceWidthPx,
          sourceHeightPx,
          pixelAspect: 1,
        },
        pos3DWorld: null,
        pos3DLocal:
          typeof wx === 'number'
            ? rotatePos3DLocal(wx, -(wy ?? 0), wz ?? 0, gravityApplied ? gR : null)
            : null,
        confidence: {
          score,
          visibility: typeof wv === 'number' ? wv : score,
          occluded: score < 0.3,
        },
        uncertainty: { sigmaPx: null, sigmaM: null },
        origin: { synthesized: false, derivation: 'sensor' },
      });
    }
    const tMs = data.t[f] * 1000;
    frames[f] = {
      streamId: opts.streamId,
      frameId: f,
      captureTime: {
        monotonicNs: Math.round(tMs * 1e6),
        wallclockMs: null,
        sourceTimeNs: null,
      },
      pose: {
        bodies: [
          {
            frameLocalId: 0,
            persistentTrackingId: null,
            landmarks,
            segmentation: null,
            skeletonId: 'mediapipe-33',
          },
        ],
        sampleTimeNs: Math.round(tMs * 1e6),
      },
      depth: null,
      camera: null,
      world: null,
      alignment: { method: 'strict-equal', maxSkewMs: 0 },
      precedingDrop: null,
    };
  }
  return { header, frames, gravityApplied };
}

/** Apply a 3x3 row-major rotation to (x, y, z) and return a
 *  Pos3DLocal in the canonical hip-centred frame. Pure helper so the
 *  mapping above stays branchless when gravity is disabled. */
function rotatePos3DLocal(
  x: number,
  y: number,
  z: number,
  m: readonly number[] | null,
): {
  x: number;
  y: number;
  z: number;
  localFrame: 'hipCentered';
  anchorId: null;
} {
  if (!m || m.length !== 9) {
    return { x, y, z, localFrame: 'hipCentered', anchorId: null };
  }
  return {
    x: m[0] * x + m[1] * y + m[2] * z,
    y: m[3] * x + m[4] * y + m[5] * z,
    z: m[6] * x + m[7] * y + m[8] * z,
    localFrame: 'hipCentered',
    anchorId: null,
  };
}

function isIdentity3x3(m: readonly number[]): boolean {
  return (
    Math.abs(m[0] - 1) < 1e-6 &&
    Math.abs(m[1]) < 1e-6 &&
    Math.abs(m[2]) < 1e-6 &&
    Math.abs(m[3]) < 1e-6 &&
    Math.abs(m[4] - 1) < 1e-6 &&
    Math.abs(m[5]) < 1e-6 &&
    Math.abs(m[6]) < 1e-6 &&
    Math.abs(m[7]) < 1e-6 &&
    Math.abs(m[8] - 1) < 1e-6
  );
}

export const LEGACY_POSE_DATA_CAPABILITIES = LEGACY_CAPABILITIES;
